/*
 * Lives in @bandway/ui-primitives — desktop + web SPA share this primitive.
 *
 * Vendored / adapted from wavesurfer.js v7.10.1.
 *
 * Original source:
 *   https://github.com/katspaugh/wavesurfer.js/blob/v7.10.1/src/renderer.ts
 *
 * Original copyright:
 *   Copyright (c) 2012-2023, katspaugh and contributors. BSD-3-Clause.
 *   Full license text: see THIRD_PARTY_NOTICES.md in repository root.
 *
 * Modifications for Band Way:
 *   - Output is an SVG string instead of a canvas draw.
 *   - Consumes our `{minPeaks, maxPeaks}` shape directly.
 *   - Drops barAlign / progress / responsive logic — single full-width render.
 *   - Trimmed normalization to a pure-function helper.
 */

const DEFAULTS = {
  barWidth:  2,
  barGap:    1,
  barRadius: 1,
  color:     '#888',
};

/**
 * Render an asymmetric min/max ribbon as bar rects.
 *
 * @param {{minPeaks: Float32Array, maxPeaks: Float32Array}} peaks
 * @param {{width: number, height: number, barWidth?: number, barGap?: number,
 *          barRadius?: number, color?: string}} opts
 * @returns {string} `<svg>...</svg>` string (or '' for empty input).
 */
export function drawBars(peaks, opts) {
  if (!peaks?.minPeaks?.length || !peaks?.maxPeaks?.length) return '';
  const w     = opts.width,  h = opts.height;
  const bw    = opts.barWidth  ?? DEFAULTS.barWidth;
  const bg    = opts.barGap    ?? DEFAULTS.barGap;
  const br    = opts.barRadius ?? DEFAULTS.barRadius;
  const color = opts.color     ?? DEFAULTS.color;
  const stride = bw + bg;
  const numBars = Math.floor(w / stride);
  if (numBars <= 0) return '';

  const cy  = h / 2;
  const amp = h / 2;
  // Group-max downsampling: each output bar reduces its proportional slice
  // of source peaks to (max of maxPeaks, min of minPeaks). Replaces the
  // previous slice-by-numBars which silently dropped tail peaks when
  // srcLen > numBars (spec 2026-05-12-waveform-bar-style-amber-design).
  const srcLen = Math.min(peaks.minPeaks.length, peaks.maxPeaks.length);
  const rects = [];
  for (let i = 0; i < numBars; i++) {
    const srcStart = Math.floor((i     * srcLen) / numBars);
    const srcEnd   = Math.min(srcLen, Math.floor(((i + 1) * srcLen) / numBars));
    if (srcEnd <= srcStart) continue;
    let maxV = peaks.maxPeaks[srcStart];
    let minV = peaks.minPeaks[srcStart];
    for (let j = srcStart + 1; j < srcEnd; j++) {
      const mv = peaks.maxPeaks[j];
      const nv = peaks.minPeaks[j];
      if (mv > maxV) maxV = mv;
      if (nv < minV) minV = nv;
    }
    const x      = i * stride;
    const yTop   = Math.round(cy - maxV * amp);
    const yBot   = Math.round(cy - minV * amp);
    const height = Math.max(1, yBot - yTop);
    rects.push(
      `<rect x="${x}" y="${yTop}" width="${bw}" height="${height}" rx="${br}" fill="${color}"/>`
    );
  }
  return `<svg viewBox="0 0 ${w} ${h}" preserveAspectRatio="none" width="100%" height="100%">${rects.join('')}</svg>`;
}

/**
 * Render a min/max envelope as a SINGLE filled area (the "solid waveform" look
 * competitors use) instead of many thin vertical bars. The bars-style render
 * (drawBars) interferes visually at high bin counts (moiré); a filled shape
 * reads clean and is far cheaper than thousands of <rect> at deep zoom.
 *
 * Traces one closed path: left→right along the TOP envelope (maxPeaks), then
 * right→left along the BOTTOM envelope (minPeaks), `Z` closed. One vertex per
 * source bin, evenly spaced across [0, width]. Vertical mapping is IDENTICAL to
 * drawBars: y = cy - v*amp with cy = amp = height/2, so v=+1→y=0 (top),
 * v=-1→y=height (bottom), v=0→y=height/2 (center). The fill occupies the same
 * vertical space as the equivalent drawBars render.
 *
 * @param {{minPeaks: Float32Array, maxPeaks: Float32Array}} peaks
 * @param {{width: number, height: number, color?: string}} opts
 * @returns {string} `<svg>...</svg>` string (or '' for empty input).
 */
export function drawWaveFill(peaks, opts) {
  if (!peaks?.minPeaks?.length || !peaks?.maxPeaks?.length) return '';
  const w     = opts.width, h = opts.height;
  const color = opts.color ?? DEFAULTS.color;
  const n     = Math.min(peaks.minPeaks.length, peaks.maxPeaks.length);
  if (n <= 0 || !(w > 0) || !(h > 0)) return '';

  const cy  = h / 2;
  const amp = h / 2;
  // y(v) = cy - v*amp — matches drawBars exactly (top=maxV, bottom=minV).
  // x(i): evenly space n vertices across the full width. With n>1 the last
  // vertex lands exactly on the right edge; n===1 collapses to x=0 (a degenerate
  // sliver, fine for a single bin).
  const span = n > 1 ? w / (n - 1) : 0;
  const fmt = (v) => Math.round(v * 100) / 100; // trim float noise in the `d` string

  // Top envelope, left → right.
  let d = `M ${fmt(0)} ${fmt(cy - peaks.maxPeaks[0] * amp)}`;
  for (let i = 1; i < n; i++) {
    d += ` L ${fmt(i * span)} ${fmt(cy - peaks.maxPeaks[i] * amp)}`;
  }
  // Bottom envelope, right → left (so the area closes cleanly).
  for (let i = n - 1; i >= 0; i--) {
    d += ` L ${fmt(i * span)} ${fmt(cy - peaks.minPeaks[i] * amp)}`;
  }
  d += ' Z';

  return `<svg viewBox="0 0 ${w} ${h}" preserveAspectRatio="none" width="100%" height="100%"><path d="${d}" fill="${color}"/></svg>`;
}

/**
 * Scale `{minPeaks, maxPeaks}` so the largest absolute extreme equals ±`target`.
 *
 * Two modes:
 *   - `target=1` (default): canonical normalization, peak hits ±1. Used when
 *     a quiet recording must render at full height (e.g. previous overview
 *     auto-scale toggle).
 *   - `target < 1`: visual-headroom mode — scale ONLY DOWN when max abs >
 *     target, leave quiet content untouched. Use this to keep loud per-track
 *     waveforms from saturating the row height, AND to fit a summed overview
 *     (where peaks can exceed 1.0) back into the SVG bounds. Relative
 *     loudness between bins is preserved.
 *
 * @param {{minPeaks: Float32Array, maxPeaks: Float32Array}} peaks
 * @param {number} [target=1] — desired peak in [0, 1]
 * @returns {{minPeaks: Float32Array, maxPeaks: Float32Array}}
 */
export function normalizePeaks(peaks, target = 1) {
  const mn = peaks.minPeaks, mx = peaks.maxPeaks;
  let maxAbs = 0;
  for (let i = 0; i < mn.length; i++) {
    const a = Math.abs(mn[i]); if (a > maxAbs) maxAbs = a;
    const b = Math.abs(mx[i]); if (b > maxAbs) maxAbs = b;
  }
  if (maxAbs === 0) return peaks;
  // target=1 → canonical normalize (always scale to ±1).
  // target<1 → headroom-fit: leave quiet content alone, only shrink loud.
  if (target >= 1 ? maxAbs === 1 : maxAbs <= target) return peaks;
  const k = target / maxAbs;
  const minOut = new Float32Array(mn.length);
  const maxOut = new Float32Array(mx.length);
  for (let i = 0; i < mn.length; i++) {
    minOut[i] = mn[i] * k;
    maxOut[i] = mx[i] * k;
  }
  return { minPeaks: minOut, maxPeaks: maxOut };
}

/**
 * Draw symmetric ribbon waveform — mirrored above/below center line.
 * Returns an `<svg>` string suitable for `innerHTML` insertion.
 *
 * Used by web SPA for per-track waveforms in Phase E.
 *
 * @param {Float32Array|number[]} peaks  Normalized peaks 0..1
 * @param {object} opts
 * @param {number} opts.width  px
 * @param {number} opts.height px
 * @param {string} [opts.fillStyle='currentColor']
 * @param {number} [opts.barWidth=2]
 * @param {number} [opts.barGap=1]
 * @returns {string}
 */
export function drawSymRibbon(peaks, opts) {
  const { width, height, fillStyle = 'currentColor', barWidth = 2, barGap = 1 } = opts;
  const stride = barWidth + barGap;
  const barCount = Math.floor(width / stride);
  const center = height / 2;
  const parts = [`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${width} ${height}" preserveAspectRatio="none">`];
  for (let i = 0; i < barCount; i++) {
    const idx = Math.floor((i / barCount) * peaks.length);
    const v = Math.abs(peaks[idx] || 0);
    const halfH = v * center;
    const x = i * stride;
    const y = center - halfH;
    const h = halfH * 2;
    parts.push(`<rect x="${x}" y="${y}" width="${barWidth}" height="${h}" fill="${fillStyle}"/>`);
  }
  parts.push('</svg>');
  return parts.join('');
}
